// ============================================================
// SheetEditor - one combatant's whole sheet, edited live.
//
// The Stats tab on the actor side and the Anatomy tab on the target
// side: the same component, in the host panel's livery. It edits the
// combatant's own SHEET (so attacks and tests follow), shows the flat
// values pinned over it, and still hands the live state (current
// wounds, cover, worn armour) to the panel's own writer.
//
// Two locks, because they are two kinds of edit: `sheetLocked` is what
// the character IS (the editSheets lock), `stateLocked` is what has
// happened to them (the editTarget lock).
// ============================================================
import { useState } from 'react';
import type { BodyShape } from '../../engine/bodyShape';
import { rebodiedState, rebodyPatch } from '../../engine/roster/rebody';
import type { SheetEditor as SheetEditorApi } from '../../hooks/useSheetEditor';
import type { Target } from '../../types';
import CharacterExportDialog from '../CharacterExportDialog';
import ChangedList from './ChangedList';
import SheetMasthead from './SheetMasthead';
import { sectionsMatching } from './sheetSearch';
import { useSheetFolds } from './SheetSection';
import { CoreSections, ListSections } from './SheetSections';
import styles from './Sheet.module.css';

interface SheetEditorProps {
  tone: 'actor' | 'target';
  /**
   * Hosted by the library editor over a draft: there is no library sheet
   * to compare against and no fight going on, so the masthead's name,
   * origin and Changed toggle are left out (the editor has its own name
   * field and its own Save).
   */
  library?: boolean;
  editor: SheetEditorApi;
  /** The combatant's projection, as the host panel shows it. */
  target: Target;
  sheetLocked: boolean;
  stateLocked: boolean;
  /** The panel's writer for the live state: current wounds, cover, worn armour, facing, the name. */
  onTargetChange: (next: Target) => void;
}

export default function SheetEditor(props: SheetEditorProps) {
  const { tone, library = false, editor, target, sheetLocked, stateLocked, onTargetChange } = props;
  const { combatant, pins, editSheet, resetPin } = editor;
  const folds = useSheetFolds();
  const [query, setQuery] = useState('');
  const [showChanged, setShowChanged] = useState(false);
  const [exporting, setExporting] = useState(false);
  if (!combatant) return null;
  const sheet = combatant.sheet;
  const ctx = { pins, folds, sheetLocked, resetPin, found: sectionsMatching(query, sheet) };
  const halves = {
    character: sheet,
    target,
    onChange: editSheet,
    onPin: editor.setPin,
    onTargetChange,
    sheetLocked,
    stateLocked,
    combatantState: combatant.state,
    atTable: !library,
  };
  const rebody = (shape: BodyShape, crowd: boolean) =>
    editSheet(rebodyPatch(sheet, shape, crowd), (state) => rebodiedState(state, shape, crowd));
  return (
    <div className={styles.sheet} data-tone={tone} data-testid="sheet-editor">
      <SheetMasthead
        library={library}
        target={target}
        editor={editor}
        disabled={sheetLocked}
        query={query}
        onQuery={setQuery}
        showChanged={showChanged}
        onShowChanged={setShowChanged}
        onTargetChange={onTargetChange}
      />
      {showChanged && <ChangedList editor={editor} disabled={sheetLocked} onExport={() => setExporting(true)} />}
      {exporting && (
        <CharacterExportDialog
          initialIds={[combatant.id]}
          canSaveToLibrary={!sheetLocked}
          onClose={() => setExporting(false)}
        />
      )}
      <CoreSections ctx={ctx} halves={halves} onRebody={rebody} />
      <ListSections ctx={ctx} halves={halves} />
    </div>
  );
}
